import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calculator, Copy, Check, Briefcase, BookOpen, HelpCircle, Coins } from "lucide-react";
import { toast } from "sonner";
import {
  CalculationResult,
  calcularLiquidacionLaboral,
  calcularPensionLey797,
  calcularPensionInvalidez,
  calcularRetroactivoPensional,
  calcularPerjuiciosTrasladoPensional,
  obtenerEVRentistas
} from "@/lib/calculators/calculatorsEngine";
import { CALCULATOR_FAQS } from "@/lib/calculators/calculatorFaqs";
import { deductCalculatorCredit } from "@/lib/calculators/deductCalculatorCredit";

// 1. Liquidación Laboral Completa
export const LiquidacionLaboralForm: React.FC = () => {
  const [nombre, setNombre] = useState("");
  const [salario, setSalario] = useState("2500000");
  const [fechaInicio, setFechaInicio] = useState("2024-01-15");
  const [fechaTerminacion, setFechaTerminacion] = useState("2026-08-01");
  const [tipoContrato, setTipoContrato] = useState<"indefinido" | "fijo" | "obra">("indefinido");
  const [causaTerminacion, setCausaTerminacion] = useState<"despido_sin_justa" | "renuncia" | "despido_justa" | "mutuo_acuerdo" | "terminacion_obra">("despido_sin_justa");

  // Campos avanzados de saldos y abonos
  const [salariosPendientes, setSalariosPendientes] = useState("0");
  const [cesantiasConsignadas, setCesantiasConsignadas] = useState("0");
  const [interesesPagados, setInteresesPagados] = useState("0");
  const [primasPagadas, setPrimasPagadas] = useState("0");
  const [diasVacDisfrutados, setDiasVacDisfrutados] = useState("0");
  const [abonosRealizados, setAbonosRealizados] = useState("0");
  const [diasMoraArt65, setDiasMoraArt65] = useState("0");

  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const sal = parseFloat(salario) || 0;

    // Intentar consulta al servicio Backend Python (legal-ai API)
    try {
      const response = await fetch("http://localhost:8000/api/v1/services/labor/calculate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fecha_ingreso: fechaInicio,
          fecha_retiro: fechaTerminacion,
          salario_mensual: sal,
          tipo_contrato: tipoContrato.toUpperCase(),
          causa_terminacion: causaTerminacion.toUpperCase(),
          incluye_auxilio_transporte: true,
          salarios_pendientes: parseFloat(salariosPendientes) || 0,
          cesantias_consignadas_historico: parseFloat(cesantiasConsignadas) || 0,
          intereses_pagados_historico: parseFloat(interesesPagados) || 0,
          primas_pagadas_historico: parseFloat(primasPagadas) || 0,
          dias_vacaciones_disfrutados: parseFloat(diasVacDisfrutados) || 0,
          abonos_o_descuentos_realizados: parseFloat(abonosRealizados) || 0,
          dias_mora_art65: parseInt(diasMoraArt65) || 0,
          nombre_trabajador: nombre
        })
      });

      if (response.ok) {
        const resJson = await response.json();
        const data = resJson.data;

        setResult({
          title: "Liquidación Jurídica de Prestaciones e Indemnización Laboral (Backend API)",
          total: Math.round(data.total_realmente_adeudado),
          summary: `Liquidación por ${data.dias_totales_laborados} días laborados | Causal: ${data.causa_terminacion}`,
          breakdown: [
            { label: "SECCIÓN A: Salarios Pendientes en Mora", value: Math.round(data.total_salarios_pendientes) },
            { label: "SECCIÓN B: Cesantías Pendientes (Causadas - Consignadas)", value: Math.round(data.total_cesantias_pendientes) },
            { label: "Intereses a las Cesantías Pendientes (12% por anualidad)", value: Math.round(data.total_intereses_pendientes) },
            { label: "Prima de Servicios Pendiente", value: Math.round(data.total_prima_pendiente) },
            { label: "SECCIÓN C: Vacaciones Pendientes (Excluye aux. transporte)", value: Math.round(data.total_vacaciones_pendientes) },
            { label: "SECCIÓN D: Indemnización por Despido Sin Justa Causa (Art. 64 CST)", value: Math.round(data.total_indemnizacion_procedente) },
            { label: "Fundamento Indemnización", value: data.indemnizacion.justificacion_legal },
            { label: "SECCIÓN E: Menos Abonos y Pagos Previos Realizados", value: -Math.round(data.total_pagos_abonos_descuentos) },
            { label: "MÓDULO INFORMATIVO: Posible Sanción Moratoria (Art. 65 CST)", value: Math.round(data.moratoria_art65.valor_estimado_art65) }
          ],
          normativa: data.fundamentos_legales
        });
        toast.success("Liquidación procesada por el motor oficial de la API");
        return;
      }
    } catch (e) {
      console.warn("Backend API not reached, using local engine fallback", e);
    }

    // Fallback al motor local TypeScript
    const res = calcularLiquidacionLaboral(sal, 929, true, tipoContrato, {
      fechaInicio,
      fechaTerminacion,
      causaTerminacion,
      salariosPendientes: parseFloat(salariosPendientes) || 0,
      cesantiasConsignadas: parseFloat(cesantiasConsignadas) || 0,
      interesesPagados: parseFloat(interesesPagados) || 0,
      primasPagadas: parseFloat(primasPagadas) || 0,
      diasVacacionesDisfrutados: parseFloat(diasVacDisfrutados) || 0,
      abonosRealizados: parseFloat(abonosRealizados) || 0,
      diasMoraArt65: parseInt(diasMoraArt65) || 0,
    });

    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["liquidacion-laboral"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Desglose por Anualidades</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Cesantías e Intereses (12% anual) desglosados por año calendario diferenciando causados de pagados.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Indemnización Art. 64</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Se activa únicamente si la causa es Despido Sin Justa Causa segun tope salarial (&lt; 10 SMLMV vs &gt;= 10 SMLMV).
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Auxilio de Transporte</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Se incluye en Cesantías y Prima de Servicios; se excluye estrictamente de Vacaciones e Indemnización.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-amber-600" /> Datos del Contrato y Causal de Terminación
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Nombre del Trabajador</Label>
            <Input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre completo"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Tipo de Contrato *</Label>
            <Select value={tipoContrato} onValueChange={(v: any) => setTipoContrato(v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="indefinido">Término Indefinido</SelectItem>
                <SelectItem value="fijo">Término Fijo</SelectItem>
                <SelectItem value="obra">Obra o Labor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Causa de Terminación *</Label>
            <Select value={causaTerminacion} onValueChange={(v: any) => setCausaTerminacion(v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="despido_sin_justa">Despido Sin Justa Causa (Activa Indemnización)</SelectItem>
                <SelectItem value="renuncia">Renuncia Voluntaria (Sin Indemnización)</SelectItem>
                <SelectItem value="despido_justa">Despido Con Justa Causa (Sin Indemnización)</SelectItem>
                <SelectItem value="mutuo_acuerdo">Mutuo Acuerdo / Conciliación</SelectItem>
                <SelectItem value="terminacion_obra">Vencimiento de Término / Obra</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Último Salario Mensual ($) *</Label>
            <Input
              type="number"
              value={salario}
              onChange={(e) => setSalario(e.target.value)}
              placeholder="Ej: 2.500.000"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Inicio *</Label>
            <Input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Terminación *</Label>
            <Input
              type="date"
              value={fechaTerminacion}
              onChange={(e) => setFechaTerminacion(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        {/* Sección de Conceptos Pagados y Saldos Pendientes */}
        <div className="border-t border-slate-100 pt-4 space-y-4">
          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
            Pagos Anteriores y Saldos a Descontar
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Salarios Pendientes / Mora ($)</Label>
              <Input
                type="number"
                value={salariosPendientes}
                onChange={(e) => setSalariosPendientes(e.target.value)}
                placeholder="0"
                className="bg-white border-slate-300 text-slate-900 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Cesantías Ya Consignadas ($)</Label>
              <Input
                type="number"
                value={cesantiasConsignadas}
                onChange={(e) => setCesantiasConsignadas(e.target.value)}
                placeholder="0"
                className="bg-white border-slate-300 text-slate-900 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Intereses Cesantías Pagados ($)</Label>
              <Input
                type="number"
                value={interesesPagados}
                onChange={(e) => setInteresesPagados(e.target.value)}
                placeholder="0"
                className="bg-white border-slate-300 text-slate-900 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Primas de Servicios Pagadas ($)</Label>
              <Input
                type="number"
                value={primasPagadas}
                onChange={(e) => setPrimasPagadas(e.target.value)}
                placeholder="0"
                className="bg-white border-slate-300 text-slate-900 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Días Vacaciones Disfrutados</Label>
              <Input
                type="number"
                value={diasVacDisfrutados}
                onChange={(e) => setDiasVacDisfrutados(e.target.value)}
                placeholder="Ej: 15"
                className="bg-white border-slate-300 text-slate-900 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-700">Abonos / Anticipos Realizados ($)</Label>
              <Input
                type="number"
                value={abonosRealizados}
                onChange={(e) => setAbonosRealizados(e.target.value)}
                placeholder="0"
                className="bg-white border-slate-300 text-slate-900 rounded-xl"
              />
            </div>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Liquidar Prestaciones e Indemnización (CST) — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <LaboralResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-amber-600" /> Preguntas Frecuentes de Liquidación Laboral
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 2. Pensión de Vejez Ley 797
export const PensionLey797Form: React.FC = () => {
  const [genero, setGenero] = useState<"masculino" | "femenino">("masculino");
  const [edad, setEdad] = useState("62");
  const [semanas, setSemanas] = useState("1400");
  const [ibl, setIbl] = useState("4500000");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const s = parseInt(semanas) || 1300;
    const e = parseInt(edad) || 62;
    const ib = parseFloat(ibl) || 0;
    setResult(calcularPensionLey797(ib, s, e, genero));
  };

  const faqs = CALCULATOR_FAQS["pension-ley797"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Edad Mínima</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            62 años de edad para hombres y 57 años para mujeres en régimen RPM.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Semanas Mínimas</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            1.300 semanas de cotización requeridas para causar el derecho.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Tasa de Reemplazo</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Fórmula regresiva r = 65.5 - 0.5*(IBL/SMLMV) + 1.5% semanas extra (Máx 80%).
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-amber-600" /> Parámetros del Pensionado
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Género / Edad Mínima *</Label>
            <Select value={genero} onValueChange={(v: any) => setGenero(v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="masculino">Masculino (62 años)</SelectItem>
                <SelectItem value="femenino">Femenino (57 años)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Edad al Pensionarse *</Label>
            <Input
              type="number"
              value={edad}
              onChange={(e) => setEdad(e.target.value)}
              placeholder="62+"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Semanas Cotizadas *</Label>
            <Input
              type="number"
              value={semanas}
              onChange={(e) => setSemanas(e.target.value)}
              placeholder="Mín. 1.300"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">IBL — Ingreso Base de Liquidación ($) *</Label>
            <Input
              type="number"
              value={ibl}
              onChange={(e) => setIbl(e.target.value)}
              placeholder="Promedio de los últimos 10 años indexado"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Calcular Pensión de Vejez Ley 797 — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <LaboralResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-amber-600" /> Preguntas Frecuentes Pensionales
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 3. Pensión de Invalidez (Detalle Completo GMH LEX)
export const PensionInvalidezForm: React.FC = () => {
  const [origen, setOrigen] = useState<"comun" | "laboral">("comun");
  const [pcl, setPcl] = useState("58");
  const [semanas, setSemanas] = useState("650");
  const [ibl, setIbl] = useState("2800000");
  const [granInvalidez, setGranInvalidez] = useState(false);
  const [fechaEstructuracion, setFechaEstructuracion] = useState("2023-05-10");
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const p = parseFloat(pcl) || 50;
    const s = parseInt(semanas) || 50;
    const ib = parseFloat(ibl) || 0;
    setResult(calcularPensionInvalidez(ib, p, s, origen, granInvalidez));
  };

  const faqs = CALCULATOR_FAQS["pension-invalidez"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Requisitos Automáticos</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Verifica PCL ≥ 50% y densidad de 50 semanas en los últimos 3 años (Ley 860 de 2003). Regla especial menor de 20 años: 26 semanas.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Monto por Origen</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Origen común (Art. 40 Ley 100): 45%-75% del IBL. Origen laboral (Ley 776/2002 ARL): 60%-75% + 15% gran invalidez.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Mora e Indemnización</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Estima intereses de mora del Art. 141 Ley 100 e indemnización sustitutiva si no se acreditan las semanas mínimas.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-amber-600" /> Datos del Dictamen y Cotización
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Origen de la Invalidez *</Label>
            <Select value={origen} onValueChange={(v: any) => setOrigen(v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="comun">Origen Común (Fondo de Pensiones Colpensiones/AFP)</SelectItem>
                <SelectItem value="laboral">Origen Laboral (ARL - Ley 776 de 2002)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Pérdida de Capacidad Laboral (PCL %) *</Label>
            <Input
              type="number"
              value={pcl}
              onChange={(e) => setPcl(e.target.value)}
              placeholder="Ej: 58.5 (Mínimo 50%)"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Fecha de Estructuración del Siniestro *</Label>
            <Input
              type="date"
              value={fechaEstructuracion}
              onChange={(e) => setFechaEstructuracion(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Semanas Cotizadas (Últimos 3 años) *</Label>
            <Input
              type="number"
              value={semanas}
              onChange={(e) => setSemanas(e.target.value)}
              placeholder="Requerido: Mín. 50 semanas"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">IBL Mensual — Ingreso Base de Liquidación ($) *</Label>
            <Input
              type="number"
              value={ibl}
              onChange={(e) => setIbl(e.target.value)}
              placeholder="Promedio del último año o de toda la vida laboral indexado"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <div className="pt-2">
          <div className="flex items-center space-x-2">
            <Checkbox id="gi" checked={granInvalidez} onCheckedChange={(c) => setGranInvalidez(Boolean(c))} />
            <Label htmlFor="gi" className="text-xs text-slate-700 cursor-pointer font-medium">
              Gran Invalidez: El afiliado requiere auxilio de otra persona para actos elementales (+15% mesada Art. 10 Ley 776)
            </Label>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Liquidar Pensión de Invalidez (Ley 860 / Ley 776) — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <LaboralResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-amber-600" /> Preguntas Frecuentes de Pensión de Invalidez
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// 4. Retroactivo Pensional (Detalle Completo GMH LEX)
export const RetroactivoPensionalForm: React.FC = () => {
  const [modalidad, setModalidad] = useState<"ipc" | "mora" | "mixto">("ipc");
  const [mesadaReal, setMesadaReal] = useState("3500000");
  const [mesadaReconocida, setMesadaReconocida] = useState("0");
  const [fechaDesde, setFechaDesde] = useState("2024-01-01");
  const [fechaHasta, setFechaHasta] = useState("2026-08-01");
  const [incluyeAdicionales, setIncluyeAdicionales] = useState(true);
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = async () => {
    const success = await deductCalculatorCredit();
    if (!success) return;

    const real = parseFloat(mesadaReal) || 0;
    const rec = parseFloat(mesadaReconocida) || 0;
    const d1 = new Date(fechaDesde);
    const d2 = new Date(fechaHasta);
    const meses = Math.max(1, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));

    const dif = Math.max(0, real - rec);
    const res = calcularRetroactivoPensional(dif, meses, modalidad, incluyeAdicionales);
    setResult(res);
  };

  const faqs = CALCULATOR_FAQS["retroactivo-pensional"] || [];

  return (
    <div className="space-y-8">
      {/* Explanatory Key Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">IPC Año a Año</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Actualiza cada mesada dejada de percibir con la variación IPC DANE para mantener el poder adquisitivo real.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Intereses Mora Art. 141</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Si se declara retardo o mora injustificada, se aplica la tasa de usura de la Superfinanciera mes a mes.
          </p>
        </div>
        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 space-y-1">
          <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Mesadas Adicionales</p>
          <p className="text-xs text-slate-600 leading-relaxed">
            Incluye automáticamente las mesadas adicionales de junio (mesada 13) y diciembre (mesada 14) en el cómputo.
          </p>
        </div>
      </div>

      {/* Main Form */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-amber-600" /> Datos del Retroactivo Pensional
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Modalidad de Actualización *</Label>
            <Select value={modalidad} onValueChange={(v: any) => setModalidad(v)}>
              <SelectTrigger className="bg-white border-slate-300 text-slate-900 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200 text-slate-900">
                <SelectItem value="ipc">Indexación IPC DANE (Preserva poder adquisitivo)</SelectItem>
                <SelectItem value="mora">Intereses de Mora (Art. 141 Ley 100 - Tasa Usura)</SelectItem>
                <SelectItem value="mixto">Mixta (Indexación IPC + Intereses Moratorios)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Mesada Real que Debió Pagarse ($) *</Label>
            <Input
              type="number"
              value={mesadaReal}
              onChange={(e) => setMesadaReal(e.target.value)}
              placeholder="Valor correcto de la mesada pensional"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Mesada Reconocida / Pagada ($)</Label>
            <Input
              type="number"
              value={mesadaReconocida}
              onChange={(e) => setMesadaReconocida(e.target.value)}
              placeholder="0 si no se reconoció o pagó ninguna mesada"
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-slate-700">Período — Fecha Inicial Causación *</Label>
            <Input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-semibold text-slate-700">Período — Fecha Corte / Inclusión Nómina *</Label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="bg-white border-slate-300 text-slate-900 rounded-xl"
            />
          </div>
        </div>

        <div className="pt-2">
          <div className="flex items-center space-x-2">
            <Checkbox id="ad" checked={incluyeAdicionales} onCheckedChange={(c) => setIncluyeAdicionales(Boolean(c))} />
            <Label htmlFor="ad" className="text-xs text-slate-700 cursor-pointer font-medium">
              Incluir mesadas adicionales de Junio (Mesada 13) y Diciembre (Mesada 14) en el cómputo del retroactivo
            </Label>
          </div>
        </div>

        <Button
          onClick={handleCalculate}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white font-black py-6 rounded-2xl uppercase tracking-wider text-xs shadow-md"
        >
          <Calculator className="w-4 h-4 mr-2" /> Calcular Retroactivo Pensional (Art. 141 Ley 100) — 1 Crédito
        </Button>
      </div>

      {/* Result View */}
      {result && <LaboralResultDisplay result={result} />}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-3">
            <HelpCircle className="w-4 h-4 text-amber-600" /> Preguntas Frecuentes de Retroactivo Pensional
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="space-y-1">
                <h4 className="text-xs font-bold text-slate-900">{faq.question}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LaboralResultDisplay: React.FC<{ result: CalculationResult }> = ({ result }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = `--- INFORME DE LIQUIDACIÓN LABORAL / PENSIONAL ---
${result.title}
${result.summary}

TOTAL CONSOLIDADO: $${result.total.toLocaleString("es-CO", { maximumFractionDigits: 0 })} COP

DESGLOSE:
${result.breakdown.map((b) => `- ${b.label}: ${typeof b.value === "number" ? "$" + b.value.toLocaleString("es-CO") : b.value}`).join("\n")}

CITA NORMATIVA:
${result.normativa.map((n) => `- ${n}`).join("\n")}

Generado en Mi Asistente Legalia`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Informe copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 rounded-2xl bg-amber-50/60 border border-amber-200 space-y-6 text-slate-900 shadow-sm animate-in fade-in">
      <div className="flex justify-between items-start">
        <div>
          <span className="text-[11px] font-extrabold text-amber-800 uppercase tracking-wider">
            Resumen Consolidado de la Liquidación
          </span>
          <div className="text-3xl md:text-4xl font-black text-slate-900 mt-1">
            ${result.total.toLocaleString("es-CO", { maximumFractionDigits: 0 })} COP
          </div>
          <p className="text-xs text-slate-600 mt-1">{result.summary}</p>
        </div>
        <Button onClick={handleCopy} variant="outline" size="sm" className="bg-white border-slate-300 text-slate-700 hover:bg-slate-50 font-bold">
          {copied ? <Check className="w-4 h-4 text-amber-600 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
          {copied ? "Copiado!" : "Copiar Informe"}
        </Button>
      </div>

      {/* Breakdown Table */}
      <div className="space-y-2">
        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
          <BookOpen className="w-4 h-4 text-amber-700" /> Desglose de Conceptos e Indemnizaciones
        </h4>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
          {result.breakdown.map((b, i) => (
            <div key={i} className="p-3.5 flex justify-between text-xs">
              <span className="text-slate-600 font-medium">{b.label}</span>
              <span className="font-bold text-slate-900">
                {typeof b.value === "number" ? `$${b.value.toLocaleString("es-CO")}` : b.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Normativa */}
      <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2">
        <h5 className="text-xs font-bold text-amber-800 uppercase tracking-wider">Fundamento Legal (CST / Ley 100)</h5>
        <ul className="text-xs text-slate-600 list-disc pl-4 space-y-1">
          {result.normativa.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// 5. Perjuicios por Traslado Pensional (Corte Suprema de Justicia - Sala Laboral)
export const PerjuiciosTrasladoPensionalForm: React.FC = () => {
  const [semanas, setSemanas] = useState<string>("950");
  const [edadTraslado, setEdadTraslado] = useState<string>("35");
  const [edadExigida, setEdadExigida] = useState<string>("62"); // 62 H, 57 M
  const [mesadaRPM, setMesadaRPM] = useState<string>("2500000");
  const [mesadaRAIS, setMesadaRAIS] = useState<string>("1600000");
  const [fechaCausacion, setFechaCausacion] = useState<string>("");
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");
  const [edadSentencia2, setEdadSentencia2] = useState<string>("");
  const [expectativaVida, setExpectativaVida] = useState<string>("21.3");
  const [mesadasAnio, setMesadasAnio] = useState<string>("13");

  // Salidas calculadas estilo oficial CSJ
  const [outPP, setOutPP] = useState<string>("—");
  const [outDiff, setOutDiff] = useState<string>("—");
  const [outPagos, setOutPagos] = useState<string>("—");
  const [outIndem, setOutIndem] = useState<string>("—");
  const [outSemanas, setOutSemanas] = useState<string>("—");
  const [outEdad, setOutEdad] = useState<string>("—");
  const [rawIndem, setRawIndem] = useState<number | null>(null);

  // Formato monetario pesos enteros sin decimales (estándar CSJ)
  const fmtMoneyFloor = (v: number) => {
    if (!Number.isFinite(v)) return "—";
    const entero = Math.trunc(v);
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(entero);
  };

  const fmtNumber = new Intl.NumberFormat("es-CO", {
    maximumFractionDigits: 4
  });

  // Autocompletar edad al momento de la causación e ingreso a nómina y expectativa de vida EV
  const autoCompletarEdadYEV = (fNac: string, fCaus: string, erVal: string) => {
    const erNum = parseFloat(erVal) || 62;
    const sexo: "H" | "M" = Math.abs(erNum - 57) < 0.5 ? "M" : "H";

    let edadFloor: number | null = null;
    if (fNac && fCaus) {
      const dNac = new Date(fNac);
      const dCaus = new Date(fCaus);
      if (!isNaN(dNac.getTime()) && !isNaN(dCaus.getTime())) {
        const ms = dCaus.getTime() - dNac.getTime();
        if (ms >= 0) {
          const dias = ms / (1000 * 60 * 60 * 24);
          const anios = dias / 365.2425;
          edadFloor = Math.floor(anios);
          setEdadSentencia2(String(edadFloor));
        } else {
          setEdadSentencia2("");
        }
      } else {
        setEdadSentencia2("");
      }
    } else {
      setEdadSentencia2("");
    }

    const edadBaseEV = edadFloor !== null ? edadFloor : erNum;
    const evCalc = obtenerEVRentistas(edadBaseEV, sexo);
    if (Number.isFinite(evCalc)) {
      setExpectativaVida(evCalc.toFixed(1));
    }
  };

  const handleCalcular = () => {
    const s = parseFloat(semanas.replace(/\./g, "").replace(",", "."));
    const ep = parseFloat(edadTraslado.replace(/\./g, "").replace(",", "."));
    const er = parseFloat(edadExigida.replace(/\./g, "").replace(",", "."));
    const rpm = parseFloat(mesadaRPM.replace(/\./g, "").replace(",", "."));
    const rais = parseFloat(mesadaRAIS.replace(/\./g, "").replace(",", "."));
    const ev = parseFloat(expectativaVida.replace(/\./g, "").replace(",", "."));
    const ma = parseInt(mesadasAnio) || 13;

    const valid = [s, ep, er, rpm, rais, ev].every((x) => Number.isFinite(x) && x >= 0);
    if (!valid) {
      setOutPP("—");
      setOutDiff("—");
      setOutPagos("—");
      setOutIndem("—");
      setOutSemanas("—");
      setOutEdad("—");
      setRawIndem(null);
      return;
    }

    const base = 0.5;
    const compSemanas = Math.pow(s / 1300, 0.5);
    const compEdad = Math.pow(2, -(Math.abs(ep - er) / 100));
    const pp = base * compSemanas * compEdad;
    const diff = rpm - rais;
    const pagos = ev * ma;
    const indem = pp * diff * pagos;

    setOutSemanas(fmtNumber.format(compSemanas));
    setOutEdad(fmtNumber.format(compEdad));
    setOutPP(`${fmtNumber.format(pp)} (${(pp * 100).toFixed(2)}%)`);
    setOutDiff(fmtMoneyFloor(diff));
    setOutPagos(`${fmtNumber.format(pagos)} mesadas`);
    setOutIndem(fmtMoneyFloor(indem));
    setRawIndem(indem);
  };

  // Ejecución inicial y reactiva
  useEffect(() => {
    handleCalcular();
  }, [semanas, edadTraslado, edadExigida, mesadaRPM, mesadaRAIS, expectativaVida, mesadasAnio]);

  const handleLimpiar = () => {
    setSemanas("");
    setEdadTraslado("");
    setEdadExigida("62");
    setMesadaRPM("");
    setMesadaRAIS("");
    setFechaCausacion("");
    setFechaNacimiento("");
    setEdadSentencia2("");
    setExpectativaVida("");
    setOutPP("—");
    setOutDiff("—");
    setOutPagos("—");
    setOutIndem("—");
    setOutSemanas("—");
    setOutEdad("—");
    setRawIndem(null);
    toast.info("Campos limpiados.");
  };

  return (
    <div className="space-y-6">
      {/* Encabezado con fórmula oficial */}
      <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200 dark:border-slate-800 pb-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-amber-500/10 text-amber-700 dark:text-amber-300">
              <Briefcase className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">
                Calculadora — Pérdida de Oportunidad Pensional
              </h2>
              <p className="text-xs text-slate-500">
                Sala de Casación Laboral de la Corte Suprema de Justicia — Relatoría
              </p>
            </div>
          </div>
          <a
            href="https://cortesuprema.gov.co/sala-de-casacion-laboral-relatoria-calculadora-de-perjuicios/"
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 dark:text-blue-400 font-bold hover:underline flex items-center gap-1 self-start sm:self-center"
          >
            Fuente Oficial CSJ ↗
          </a>
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-300">
          <span className="font-semibold text-slate-700 dark:text-slate-200">Indemnización = </span>
          <span className="inline-block px-2.5 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 font-mono font-bold text-[11px] border border-indigo-200 dark:border-indigo-800">
            PP × (MesadaRPM − MesadaRAIS) × (EVaños × 13)
          </span>
        </div>
      </div>

      {/* Grid Principal: 2 Columnas (1.2fr y 0.8fr) estilo CSJ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* COLUMNA IZQUIERDA: Formulario de Variables */}
        <div className="lg:col-span-7 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 md:p-6 shadow-sm space-y-5">
          <div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white">Ingreso de variables</h3>
            <p className="text-xs text-slate-500">Complete los campos y presione <strong>Calcular</strong>.</p>
          </div>

          {/* Bloque 1: Condición pensional (PP) */}
          <div className="space-y-3 pt-2">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
              Condición pensional (PP)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="semanas" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Semanas cotizadas (S)
                </Label>
                <Input
                  id="semanas"
                  type="text"
                  placeholder="p. ej., 950 o 950,5"
                  value={semanas}
                  onChange={(e) => setSemanas(e.target.value)}
                  className="mt-1 h-10 text-sm"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Total de semanas cotizadas al momento del traslado.
                </p>
              </div>

              <div>
                <Label htmlFor="edadPens" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Edad al momento de trasladarse (EP)
                </Label>
                <Input
                  id="edadPens"
                  type="text"
                  placeholder="p. ej., 35"
                  value={edadTraslado}
                  onChange={(e) => setEdadTraslado(e.target.value)}
                  className="mt-1 h-10 text-sm"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Edad efectiva al momento del traslado.
                </p>
              </div>

              <div>
                <Label htmlFor="edadReq" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Edad exigida por la ley (ER)
                </Label>
                <Select
                  value={edadExigida}
                  onValueChange={(val) => {
                    setEdadExigida(val);
                    autoCompletarEdadYEV(fechaNacimiento, fechaCausacion, val);
                  }}
                >
                  <SelectTrigger id="edadReq" className="mt-1 h-10 text-sm">
                    <SelectValue placeholder="57 o 62" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="62">62 (Hombre)</SelectItem>
                    <SelectItem value="57">57 (Mujer)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Según sexo y régimen (57 o 62, según corresponda).
                </p>
              </div>
            </div>
          </div>

          {/* Bloque 2: Brecha económica mensual */}
          <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
              Brecha económica mensual
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="mesadaRPM" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Mesada en RPM
                </Label>
                <Input
                  id="mesadaRPM"
                  type="text"
                  placeholder="p. ej., 2.500.000 o 2500000"
                  value={mesadaRPM}
                  onChange={(e) => setMesadaRPM(e.target.value)}
                  className="mt-1 h-10 text-sm"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Valor estimado de la mesada si estuviese en RPM.
                </p>
              </div>

              <div>
                <Label htmlFor="mesadaRAIS" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Mesada en RAIS
                </Label>
                <Input
                  id="mesadaRAIS"
                  type="text"
                  placeholder="p. ej., 1.600.000 o 1600000"
                  value={mesadaRAIS}
                  onChange={(e) => setMesadaRAIS(e.target.value)}
                  className="mt-1 h-10 text-sm"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Mesada que recibe (o recibiría) en RAIS.
                </p>
              </div>
            </div>
          </div>

          {/* Bloque 3: Datos para expectativa de vida (EV) */}
          <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
              Datos para expectativa de vida (EV)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="fechaSentencia2" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Fecha de causación del derecho y de ingreso a nómina de pensionados
                </Label>
                <Input
                  id="fechaSentencia2"
                  type="date"
                  value={fechaCausacion}
                  onChange={(e) => {
                    setFechaCausacion(e.target.value);
                    autoCompletarEdadYEV(fechaNacimiento, e.target.value, edadExigida);
                  }}
                  className="mt-1 h-10 text-sm"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Fecha de causación del derecho y de ingreso a nómina de pensionados.
                </p>
              </div>

              <div>
                <Label htmlFor="fechaNacimiento" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Fecha de nacimiento
                </Label>
                <Input
                  id="fechaNacimiento"
                  type="date"
                  value={fechaNacimiento}
                  onChange={(e) => {
                    setFechaNacimiento(e.target.value);
                    autoCompletarEdadYEV(e.target.value, fechaCausacion, edadExigida);
                  }}
                  className="mt-1 h-10 text-sm"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Fecha de nacimiento de la persona.
                </p>
              </div>

              <div>
                <Label htmlFor="edadSentencia2" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Edad al momento de la causación del derecho y del ingreso a nómina de pensionados (años)
                </Label>
                <Input
                  id="edadSentencia2"
                  type="text"
                  placeholder="Se calcula automáticamente"
                  value={edadSentencia2}
                  readOnly
                  className="mt-1 h-10 text-sm bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Se calcula con base en la fecha de nacimiento y la fecha de causación del derecho y de ingreso a nómina de pensionados, y se redondea hacia abajo (entero).
                </p>
              </div>
            </div>
          </div>

          {/* Bloque 4: Pagos esperados en el tiempo */}
          <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
            <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
              Pagos esperados en el tiempo
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ev" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Expectativa de vida (años)
                </Label>
                <Input
                  id="ev"
                  type="text"
                  placeholder="p. ej., 26,8 o 26.8"
                  value={expectativaVida}
                  onChange={(e) => setExpectativaVida(e.target.value)}
                  className="mt-1 h-10 text-sm"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  La expectativa de vida se calcula con base a la Resolución 1555 de 2010.
                </p>
              </div>

              <div>
                <Label htmlFor="mesadasAnio" className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Mesadas al año
                </Label>
                <Input
                  id="mesadasAnio"
                  type="number"
                  value={mesadasAnio}
                  readOnly
                  className="mt-1 h-10 text-sm bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                />
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  El sistema reconoce 13 mesadas por año.
                </p>
              </div>
            </div>
          </div>

          {/* Botones estilo oficial CSJ */}
          <div className="flex items-center gap-3 pt-3">
            <Button
              type="button"
              onClick={handleCalcular}
              className="bg-sky-500 hover:bg-sky-600 text-white font-bold px-6 py-2.5 rounded-xl shadow-sm text-sm"
            >
              Calcular
            </Button>
            <Button
              type="button"
              onClick={handleLimpiar}
              variant="outline"
              className="bg-slate-900 hover:bg-slate-800 text-white border-slate-900 font-bold px-6 py-2.5 rounded-xl text-sm"
            >
              Limpiar
            </Button>
          </div>
        </div>

        {/* COLUMNA DERECHA: Resultados & Desglose de PP */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-2">
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Resultado</h3>
              {rawIndem !== null && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-slate-500 hover:text-slate-900"
                  onClick={() => {
                    const texto = `RESULTADO OFICIAL CORTE SUPREMA DE JUSTICIA\nProbabilidad Pensional (PP): ${outPP}\nDiferencia mensual: ${outDiff}\nPagos esperados: ${outPagos}\nIndemnización estimada: ${outIndem}\nDesglose de PP:\n- Base: 0.5\n- Componente semanas: ${outSemanas}\n- Componente edad: ${outEdad}`;
                    navigator.clipboard.writeText(texto);
                    toast.success("Memoria de cálculo copiada");
                  }}
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                </Button>
              )}
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between items-baseline p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                <span className="text-slate-500 dark:text-slate-400">Probabilidad Pensional (PP)</span>
                <strong className="text-sm font-black text-slate-900 dark:text-white">{outPP}</strong>
              </div>

              <div className="flex justify-between items-baseline p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                <span className="text-slate-500 dark:text-slate-400">Diferencia mensual (MesadaRPM − MesadaRAIS)</span>
                <strong className="text-sm font-black text-slate-900 dark:text-white">{outDiff}</strong>
              </div>

              <div className="flex justify-between items-baseline p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                <span className="text-slate-500 dark:text-slate-400">Pagos esperados (EVaños × 13)</span>
                <strong className="text-sm font-black text-slate-900 dark:text-white">{outPagos}</strong>
              </div>

              {/* KPI Good / Destacado verde */}
              <div className="flex justify-between items-baseline p-3.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-900 dark:text-emerald-200">
                <span className="text-emerald-700 dark:text-emerald-300 font-semibold">Indemnización estimada</span>
                <strong className="text-lg font-black text-emerald-600 dark:text-emerald-400">{outIndem}</strong>
              </div>
            </div>

            {/* Desglose de PP */}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
              <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Desglose de PP
              </h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between items-baseline p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                  <span className="text-slate-500 dark:text-slate-400">Base por coexistencia de regímenes</span>
                  <strong className="font-bold text-slate-800 dark:text-slate-200">0.5</strong>
                </div>

                <div className="flex justify-between items-baseline p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                  <span className="text-slate-500 dark:text-slate-400">Componente semanas (S/1300)<sup>0.5</sup></span>
                  <strong className="font-bold text-slate-800 dark:text-slate-200">{outSemanas}</strong>
                </div>

                <div className="flex justify-between items-baseline p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
                  <span className="text-slate-500 dark:text-slate-400">Componente edad 2<sup>−|EP−ER|/100</sup></span>
                  <strong className="font-bold text-slate-800 dark:text-slate-200">{outEdad}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECCIÓN INFERIOR: Explicación idéntica a la CSJ */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 md:p-6 shadow-sm space-y-3 text-xs text-slate-700 dark:text-slate-300">
        <h3 className="text-base font-bold text-slate-900 dark:text-white">Explicación</h3>
        <p>
          El componente de edad usa el denominador <strong>100</strong>. La fórmula adoptada es:
        </p>
        <div className="bg-slate-950 text-slate-100 p-3.5 rounded-xl font-mono text-xs overflow-x-auto">
          <code>PP = 0.5 × (S/1300)⁰·⁵ × 2^[EP − ER]/100</code>
        </div>

        <p className="pt-1">La indemnización se determina como:</p>
        <div className="bg-slate-950 text-slate-100 p-3.5 rounded-xl font-mono text-xs overflow-x-auto">
          <code>Indemnización = PP × (MesadaRPM − MesadaRAIS) × (EVaños × 13)</code>
        </div>

        <details className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3.5 mt-2 cursor-pointer">
          <summary className="font-bold text-slate-800 dark:text-slate-200">Variables</summary>
          <ul className="list-disc pl-5 pt-2 space-y-1 text-slate-600 dark:text-slate-300">
            <li><strong>S</strong>: semanas al traslado.</li>
            <li><strong>EP</strong>: edad al momento de trasladarse.</li>
            <li><strong>ER</strong>: edad exigida por ley (57 o 62).</li>
            <li><strong>EVaños</strong>: expectativa de vida en años.</li>
          </ul>
        </details>

        <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-100 dark:border-slate-800">
          © Calculadora de referencia para apoyo académico y técnico — Sala de Casación Laboral de la Corte Suprema de Justicia.
        </p>
      </div>
    </div>
  );
};
